import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNoties } from '../../../modules/auth';
import useInterval from '../../../utils/useInterval';
import NotiBox from '../../../components/common/header/NotiBox';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../../../lib/api/auth';
import * as domainApi from '../../../lib/api/domain';
import * as shipmentApi from '../../../lib/api/shipmentMgmt';
import IgnoreReasonDialog from '../../../components/common/header/IgnoreReasonDialog';
import {
  buildMenuLinkSet,
  resolveNotificationNavigateUrl,
} from '../../../utils/notificationLink';

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const NotiContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuLinks, setMenuLinks] = useState(null);
  // 무시 사유를 받을 대상. 브라우저 기본 prompt 는 화면과 이질적이라 앱 다이얼로그를 쓴다.
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNoties = async (checkedNoties) => {
    //console.log(checkedNoties,'chekedNoties');

    if (checkedNoties.length === 0) {
      enqueueSnackbar('선택된 알림이 없습니다.', {
        variant: 'error',
      });
      return;
    }

    try {
      // let dataToSubmit = {arr:checkedNoties};
      //console.log(checkedNoties.toString());
      const res = await authApi.confirmNoties(checkedNoties.toString());
      if (res.data.result === 'success') {
        dispatch(getNoties());
        handleClose();
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };
  
  const handleNoti = async (noti) => {
    try {
      // let dataToSubmit = {arr:checkedNoties};
      //console.log(checkedNoties.toString());
      const res = await authApi.confirmNoties(noti.notiId.toString());
      if (res.data.result === 'success') {
        let currentMenuLinks = menuLinks;
        if (!currentMenuLinks) {
          try {
            const menuRes = await domainApi.checkAuth();
            currentMenuLinks = buildMenuLinkSet(menuRes.data?.list || []);
            setMenuLinks(currentMenuLinks);
          } catch (menuErr) {
            currentMenuLinks = null;
          }
        }

        const targetUrl = resolveNotificationNavigateUrl(noti, {
          user: user || readStoredUser(),
          menuLinks: currentMenuLinks,
        });

        handleClose();
        dispatch(getNoties());
        if (targetUrl !== noti.notiUrl && targetUrl !== noti.linkUrl) {
          enqueueSnackbar('접근 가능한 화면으로 이동합니다.', {
            variant: 'info',
          });
        }
        navigate(targetUrl);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  /**
   * 이 알림을 만들어내는 조건 자체를 잠시 멈춘다.
   *
   * 읽음 처리는 알림 한 줄을 치울 뿐이라 다음 주기에 같은 알림이 또 생긴다.
   * 그래서 스누즈/무시를 한 뒤에는 지금 떠 있는 알림도 함께 읽음 처리해준다.
   * 안 그러면 "멈췄는데 목록에 그대로 남아있네" 가 된다.
   */
  const silenceNoti = async (noti, action) => {
    try {
      await action();
      await authApi.confirmNoties(noti.notiId.toString());
      dispatch(getNoties());
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const handleSnooze = (noti) =>
    silenceNoti(noti, async () => {
      await shipmentApi.snoozeShipmentCandidate(noti.targetId, 3);
      enqueueSnackbar('3일 뒤에 다시 알려드립니다.', { variant: 'success' });
    });

  // 사유를 남겨야 나중에 이 건을 어떻게 할지 판단할 근거가 생긴다. 서버에서도 필수다.
  const handleIgnore = (noti) => setIgnoreTarget(noti);

  // 여러 건을 한 번에. 같은 이유로 도는 경우가 대부분이라 사유는 한 번만 받는다.
  const handleIgnoreAll = (notis) => setIgnoreTarget({ bulk: notis || [] });

  const handleIgnoreConfirm = async (reason) => {
    const target = ignoreTarget;
    setIgnoreTarget(null);
    if (!target) return;

    // 일괄 처리는 한 번의 호출로 묶는다. 건마다 부르면 중간 실패 시 일부만 꺼진 상태가 된다.
    if (target.bulk) {
      const notis = target.bulk;
      if (notis.length === 0) return;
      try {
        await shipmentApi.ignoreShipmentCandidates(
          notis.map((n) => n.targetId),
          reason,
        );
        await authApi.confirmNoties(notis.map((n) => n.notiId).join(','));
        enqueueSnackbar(`${notis.length}건은 더 이상 알리지 않습니다.`, { variant: 'success' });
        dispatch(getNoties());
      } catch (err) {
        const message =
          typeof err.response !== 'undefined' ? err.response.data.message : err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
      return;
    }

    await silenceNoti(target, async () => {
      await shipmentApi.ignoreShipmentCandidate(target.targetId, reason);
      enqueueSnackbar('이 건은 더 이상 알리지 않습니다.', { variant: 'success' });
    });
  };

  const handleNotiAll = async () => {
    try {
      // let dataToSubmit = {arr:checkedNoties};
      //console.log(checkedNoties.toString());
      const res = await authApi.confirmNotiAll();
      if (res.data.result === 'success') {
        handleClose();
        dispatch(getNoties()); 
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };
  const { noties, notiCount, user } = useSelector(({ auth }) => ({
    noties: auth.noties,
    notiCount: auth.notiCount,
    user: auth.user,
  }));

  useEffect(() => {
    dispatch(getNoties());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterval(() => {
    // Your custom logic here
    dispatch(getNoties());
  }, 1000 * 60 * 10); //10분마다

  const handleRefresh = () => {
    dispatch(getNoties());
    enqueueSnackbar('노티를 갱신하였습니다.', {
      variant: 'info',
    });
  };

  // noties, notiCount, onConfirmNoties, onRefresh
  return (
    <>
    <NotiBox
      noties={noties}
      notiCount={notiCount}
      onConfirmNoties={handleNoti}
      onConfirmNotiAll={handleNotiAll}
      // anchorEl={anchorEl}
      // handleClick={handleClick}
      // handleClose={handleClose}
      onRefresh={handleRefresh}
      onSnooze={handleSnooze}
      onIgnore={handleIgnore}
      onIgnoreAll={handleIgnoreAll}
    />
    <IgnoreReasonDialog
      open={Boolean(ignoreTarget)}
      notiTxt={ignoreTarget?.notiTxt}
      bulkCount={ignoreTarget?.bulk?.length}
      onClose={() => setIgnoreTarget(null)}
      onConfirm={handleIgnoreConfirm}
    />
    </>
  );
};

export default NotiContainer;
